import React, { useState, useCallback, useRef } from 'react'
import { CheckIcon, ClipboardIcon } from '@bizfirst/common-react'
import { JSONVisualizer } from '@edge-stream/app-ui-components-react'

interface FormData {
  name: string
  email: string
  category: string
  message: string
}

/**
 * FormsPage Component
 *
 * Displays form submission interface with validation.
 * Single responsibility: Manage form state and submissions.
 *
 * Integration Points:
 * - Form data captured and stored
 * - Submissions published via CommManager with context
 * - Form state managed via integration store
 * - Updates received via PlayerManager
 */
export default function FormsPage(): React.ReactElement {
  const userIdRef = useRef(`user-${Math.random().toString(36).substr(2, 9)}`)
  const sessionIdRef = useRef(`sess-${Math.random().toString(36).substr(2, 9)}`)
  const formIdRef = useRef(`form-${Math.random().toString(36).substr(2, 9)}`)

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    category: 'general',
    message: '',
  })

  const [submitted, setSubmitted] = useState(false)
  const [errors, setErrors] = useState<Partial<FormData>>({})

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<FormData> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData])

  const handleChange = useCallback(
    (field: keyof FormData, value: string) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }))
      // Clear error for this field when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }))
      }
    },
    [errors],
  )

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()

      if (!validateForm()) {
        return
      }

      // Form would be submitted via CommManager with context
      console.log('Submitting form:', formData)
      setSubmitted(true)

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          category: 'general',
          message: '',
        })
        setSubmitted(false)
      }, 3000)
    },
    [formData, validateForm],
  )

  const handleReset = useCallback(() => {
    setFormData({
      name: '',
      email: '',
      category: 'general',
      message: '',
    })
    setErrors({})
    setSubmitted(false)
  }, [])

  return (
    <div className="page-container">
      <div className="page-title">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
        Forms
      </div>
      <p className="page-description">
        Submit forms with automatic entity context attachment and validation.
      </p>

      <div style={{ maxWidth: '600px' }}>
        {submitted ? (
          <div className="form-success-panel">
            <h3 style={{ margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '8px', color: '#10b981' }}>
              <CheckIcon size={20} /> Form Submitted
            </h3>
            <p style={{ margin: 0 }}>
              Your form has been submitted with full context (user, session,
              entity).
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="card">
            <div className="form-group">
              <label className="form-label">
                Name <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <input
                type="text"
                className="form-input"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Your name"
                style={
                  errors.name
                    ? { borderColor: 'var(--color-error)', borderWidth: '2px' }
                    : undefined
                }
              />
              {errors.name && (
                <div style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '4px' }}>
                  {errors.name}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Email <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <input
                type="email"
                className="form-input"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="your.email@example.com"
                style={
                  errors.email
                    ? { borderColor: 'var(--color-error)', borderWidth: '2px' }
                    : undefined
                }
              />
              {errors.email && (
                <div style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '4px' }}>
                  {errors.email}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Category</label>
              <select
                className="form-select"
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
              >
                <option value="general">General Inquiry</option>
                <option value="support">Support Request</option>
                <option value="feedback">Feedback</option>
                <option value="bug">Bug Report</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                Message <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <textarea
                className="form-textarea"
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                placeholder="Your message..."
                style={
                  errors.message
                    ? { borderColor: 'var(--color-error)', borderWidth: '2px' }
                    : undefined
                }
              />
              {errors.message && (
                <div style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '4px' }}>
                  {errors.message}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
              <button type="submit" className="button">
                Submit Form
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="button button-secondary"
              >
                Reset
              </button>
            </div>

            <div className="form-context-panel">
              <div className="form-context-label"><ClipboardIcon size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Context Attached (JSON)</div>
              <JSONVisualizer
                data={{
                  user: {
                    id: userIdRef.current,
                    name: formData.name || '',
                    permissions: ['read', 'write', 'submit'],
                  },
                  session: {
                    id: sessionIdRef.current,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    locale: navigator.language,
                  },
                  entity: {
                    type: 'form',
                    id: formData.name ? 'selected' : 'none',
                    name: 'Form Submission Entity',
                  },
                  metadata: {
                    formID: formIdRef.current,
                    submissionTimestamp: new Date().toISOString(),
                    fields: Object.keys(formData).length,
                  },
                }}
              />
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
